import isNumberArray from '@/common/utils/is-number-array';
import { FilterPrimitive, IndexedFilterValue, FilterRange } from '../types/filters';
import { lowerCase, partition, upperFirst } from 'lodash-es';
import { isNotInventoryFilterColor } from './is-inventory-filter-color';
import { isTypeNumberRange } from './filter-menu-type-guards';
import { abbreviateNumberLabel } from './abbreviate-number-label';

export interface FilterLabelLookup {
  key: string;
  subKey?: string;
  value: FilterPrimitive | FilterPrimitive[] | FilterRange<number>;
}

export interface FilterLabelValue {
  label: string;
  filter: FilterLabelLookup;
}

interface IndexedFilters {
  [key: string]: IndexedFilterValue;
}

interface OthersOptions {
  [key: string]: OthersOption;
}

interface OthersOption {
  label: string;
  predicate: (value: FilterPrimitive) => boolean;
}

export const OTHERS_OPTIONS: OthersOptions = {
  exterior_color: {
    label: 'Other Exterior Colors',
    predicate: isNotInventoryFilterColor,
  },
  interior_color: {
    label: 'Other Interior Colors',
    predicate: isNotInventoryFilterColor,
  },
};

/**
 * Creates a range label for numeric arrays, handling both ranges and single values
 * @param values - Array of numbers (length >= 1)
 * @param key - Filter key for label formatting
 * @returns FilterLabelValue with formatted range or single value label
 * @throws Error if values array is empty
 */
function createNumericRangeLabel(values: number[], key: string): FilterLabelValue {
  const sortedValues = [...values].sort((a, b) => a - b);
  const formattedKey = !['model_year'].includes(key)
    ? upperFirst(lowerCase(key))
    : undefined;

  const min = sortedValues[0];

  // Handle single value case
  if (sortedValues.length === 1) {
    const label = formattedKey ? `${formattedKey} ${min}` : `${min}`;
    return {
      label,
      filter: {
        key,
        value: min,
      },
    };
  }

  // Handle range case

  const max = sortedValues[sortedValues.length - 1];

  const label = formattedKey ? `${formattedKey} ${min} - ${max}` : `${min} - ${max}`;

  return {
    label,
    filter: {
      key,
      value: min,
    },
  };
}

const RANGE_UNITS: {
  [key: string]: {
    unit: string;
    type: 'prefix' | 'suffix';
  };
} = {
  mileage: {
    unit: 'mi',
    type: 'suffix',
  },
  monthly_price: {
    unit: '$',
    type: 'prefix',
  },
};

function createNumberRangeLabel(
  range: FilterRange<number>,
  key: string
): FilterLabelValue {
  const labelPrefix = key === 'miles_per_gallon' ? 'MPG' : upperFirst(lowerCase(key));
  const [minValue, maxValue] = Object.entries(range).map(([, value]) => {
    const prefix = RANGE_UNITS[key]?.type === 'prefix' ? RANGE_UNITS[key].unit : '';
    const suffix = RANGE_UNITS[key]?.type === 'suffix' ? ' ' + RANGE_UNITS[key].unit : '';
    return `${prefix}${abbreviateNumberLabel(value)}${suffix}`;
  });
  return {
    label: `${labelPrefix}: ${minValue} - ${maxValue}`,
    filter: {
      key,
      value: range,
    },
  };
}

function appendSuffixToLabel(value: FilterPrimitive, categoryName: string): string {
  switch (categoryName) {
    case 'exterior_color':
      return `${value} (Exterior)`;
    case 'interior_color':
      return `${value} (Interior)`;
    default:
      return `${value}`;
  }
}

function transformValueToSpecialLabel(
  value: string | number,
  filterKey: string
): FilterLabelValue {
  switch (value) {
    case 'local_only':
      return {
        label: 'Local cars only',
        filter: {
          key: filterKey,
          value: value,
        },
      };
    // TODO: remove these once fuel type data is fixed
    case 'G':
      return {
        label: 'Gasoline',
        filter: {
          key: filterKey,
          value: value,
        },
      };
    case 'E':
      return {
        label: 'Electric',
        filter: {
          key: filterKey,
          value: value,
        },
      };
    default:
      return {
        label: appendSuffixToLabel(value, filterKey),
        filter: {
          key: filterKey,
          value: value,
        },
      };
  }
}

function transformValuesToSpecialLabels(
  values: FilterPrimitive[],
  filterKey: string
): FilterLabelValue[] {
  // Safety check to only display "Others" option when there is a corresponding predicate so as not to break the "Others" option
  // when the predicate was not added by accident.
  if (OTHERS_OPTIONS[filterKey] && OTHERS_OPTIONS[filterKey].predicate !== undefined) {
    const [others, displayed] = partition(values, OTHERS_OPTIONS[filterKey].predicate);

    const labels = displayed.map((value) =>
      transformValueToSpecialLabel(value, filterKey)
    );

    return others.length > 0
      ? labels.concat({
          label: OTHERS_OPTIONS[filterKey].label,
          filter: {
            key: filterKey,
            value: others,
          },
        })
      : labels;
  } else {
    return values.map((value) => transformValueToSpecialLabel(value, filterKey));
  }
}

/**
 * Transforms an IndexedFilters object into an array of FilterLabelValue objects.
 * Handles nested objects, numeric arrays (as ranges), and primitive arrays.
 * @param filters - Object containing filter configurations
 * @returns Array of transformed filter label values with consistent structure
 */
export default function parseIndexedFilterForLabel(
  filters: IndexedFilters
): FilterLabelValue[] {
  return Object.entries(filters).flatMap(([key, value]): FilterLabelValue[] => {
    // Handle nested objects (e.g., makes -> models)
    if (typeof value === 'object' && !Array.isArray(value) && !isTypeNumberRange(value)) {
      return Object.entries(value).flatMap(([parent, children]) => {
        // Create entry for the parent (e.g. make)
        const parentEntry: FilterLabelValue = {
          label: parent,
          filter: { key, value: parent },
        };

        // Create entries for children (e.g. models)
        const childEntries = (Array.isArray(children) ? children : [children]).map(
          (child) => ({
            label: child,
            filter: { key, value: child },
          })
        );

        return [parentEntry, ...childEntries];
      });
    }

    // Handle number ranges
    if (typeof value === 'object' && isTypeNumberRange(value)) {
      return [createNumberRangeLabel(value, key)];
    }

    // Handle arrays with special processing for numeric arrays and color filters
    if (Array.isArray(value) && value.length > 0) {
      if (isNumberArray(value)) {
        return [createNumericRangeLabel(value, key)];
      }

      return transformValuesToSpecialLabels(value, key);
    }

    return [];
  });
}
